// Thin wrappers around the new /api/admin/* endpoints. All routes are
// guarded server-side; we just shape the requests and unwrap the
// {success, data} envelope.

import { GET } from '../factory/Http';
import axios from 'axios';
import Const from '../utils/Constants';

export const apiBase = (process.env.REACT_APP_API_BASE_URL)
    ? process.env.REACT_APP_API_BASE_URL
    : '/api';

function unwrap(res) {
    return (res && res.data !== undefined) ? res.data : res;
}

export async function getOverview() {
    return unwrap(await GET(`${apiBase}/admin/overview`));
}

export async function getUsers({ q = '', page = 1, size = 50 } = {}) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    params.set('page', page);
    params.set('size', size);
    return unwrap(await GET(`${apiBase}/admin/users?${params.toString()}`));
}

export async function getUser(id) {
    return unwrap(await GET(`${apiBase}/admin/users/${id}`));
}

export async function patchUserAccessOverride(userId, override) {
    const res = await axios.patch(
        `${apiBase}/admin/users/${userId}/access-override`,
        { override },
        { headers: authHeaders() },
    );
    return unwrapAxios(res);
}

export async function getSubscriptions({ status = 'all', page = 1, size = 50 } = {}) {
    const params = new URLSearchParams();
    params.set('status', status);
    params.set('page', page);
    params.set('size', size);
    return unwrap(await GET(`${apiBase}/admin/subscriptions?${params.toString()}`));
}

export async function getEngagement({ days = 30 } = {}) {
    return unwrap(await GET(`${apiBase}/admin/engagement?days=${days}`));
}

export async function getContentConversations({ q = '', status = 'all', miniCourseId = null, page = 1, size = 50 } = {}) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (status && status !== 'all') params.set('status', status);
    if (miniCourseId != null) params.set('mini_course_id', miniCourseId);
    params.set('page', page);
    params.set('size', size);
    return unwrap(await GET(`${apiBase}/admin/content/conversations?${params.toString()}`));
}

export async function getContentConversation(id) {
    return unwrap(await GET(`${apiBase}/admin/content/conversations/${id}`));
}

function authHeaders() {
    const token = localStorage.getItem(Const.AUTH_TOKEN);
    return {
        'Content-Type': 'application/json',
        'Authentication-Token': token,
        'Authorization': 'Bearer ' + token,
    };
}

function unwrapAxios(res) {
    return res.data && res.data.data !== undefined ? res.data.data : res.data;
}

export async function patchLine(id, fields) {
    // Http.js doesn't expose PATCH; do it via axios directly with the
    // same auth-header strategy.
    const res = await axios.patch(`${apiBase}/admin/content/lines/${id}`, fields, { headers: authHeaders() });
    return unwrapAxios(res);
}

export async function patchConversation(id, fields) {
    const res = await axios.patch(`${apiBase}/admin/content/conversations/${id}`, fields, { headers: authHeaders() });
    return unwrapAxios(res);
}

export async function createConversation(payload) {
    const res = await axios.post(`${apiBase}/admin/content/conversations`, payload, { headers: authHeaders() });
    return unwrapAxios(res);
}

export async function getChapterContent() {
    return unwrap(await GET(`${apiBase}/admin/chapter-content`));
}

export async function putChapterContent(chapter, fields) {
    const res = await axios.put(`${apiBase}/admin/chapter-content/${chapter}`, fields, { headers: authHeaders() });
    return unwrapAxios(res);
}

export async function getMiniCourses() {
    return unwrap(await GET(`${apiBase}/admin/content/mini-courses`));
}

export async function getMiniCourse(id) {
    return unwrap(await GET(`${apiBase}/admin/content/mini-courses/${id}`));
}

export async function createMiniCourse({ title, description }) {
    const res = await axios.post(`${apiBase}/admin/content/mini-courses`, { title, description }, { headers: authHeaders() });
    return unwrapAxios(res);
}

export async function patchMiniCourse(id, fields) {
    const res = await axios.patch(`${apiBase}/admin/content/mini-courses/${id}`, fields, { headers: authHeaders() });
    return unwrapAxios(res);
}

export async function generateTranslation(lineId, opts = {}) {
    const res = await axios.post(
        `${apiBase}/admin/content/lines/${lineId}/generate-translation`,
        opts,
        { headers: authHeaders() },
    );
    return unwrapAxios(res);
}

export async function generateAudio(lineId, opts = {}) {
    const res = await axios.post(
        `${apiBase}/admin/content/lines/${lineId}/generate-audio`,
        opts,
        { headers: authHeaders() },
    );
    return unwrapAxios(res);
}

export async function populateLineActors(convId) {
    const res = await axios.post(
        `${apiBase}/admin/content/conversations/${convId}/populate-line-actors`,
        {},
        { headers: authHeaders() },
    );
    return unwrapAxios(res);
}

// Generate audio for a whole conversation in one ElevenLabs Text to
// Dialogue request; the backend slices it into per-line offsets.
export async function generateDialogue(convId) {
    const res = await axios.post(
        `${apiBase}/admin/content/conversations/${convId}/generate-dialogue`,
        {},
        { headers: authHeaders() },
    );
    return unwrapAxios(res);
}

// Recompute per-line timings from the existing dialogue WAV(s) — no
// ElevenLabs call (re-runs the silence splitter).
export async function resliceDialogue(convId) {
    const res = await axios.post(
        `${apiBase}/admin/content/conversations/${convId}/reslice-dialogue`,
        {},
        { headers: authHeaders() },
    );
    return unwrapAxios(res);
}

export async function getSpeakers() {
    return unwrap(await GET(`${apiBase}/admin/content/speakers`));
}

export async function getElevenLabsVoices() {
    return unwrap(await GET(`${apiBase}/admin/content/elevenlabs/voices`));
}

export async function getTtsPromptingTips() {
    return unwrap(await GET(`${apiBase}/admin/tts/prompting-tips`));
}

export async function createSpeaker(payload) {
    const res = await axios.post(`${apiBase}/admin/content/speakers`, payload, { headers: authHeaders() });
    return unwrapAxios(res);
}

export async function patchSpeaker(id, fields) {
    const res = await axios.patch(`${apiBase}/admin/content/speakers/${id}`, fields, { headers: authHeaders() });
    return unwrapAxios(res);
}

export async function deleteSpeaker(id) {
    const res = await axios.delete(`${apiBase}/admin/content/speakers/${id}`, { headers: authHeaders() });
    return unwrapAxios(res);
}

export async function getZohoLog({ userId, page = 1, size = 50 } = {}) {
    const params = new URLSearchParams();
    if (userId) params.set('user_id', userId);
    params.set('page', page);
    params.set('size', size);
    return unwrap(await GET(`${apiBase}/admin/zoho/log?${params.toString()}`));
}
